/** Detect and convert YouTube / SoundCloud URLs to embeddable iframe src strings. */

export type MediaType = 'youtube' | 'soundcloud' | 'audio' | 'none';

/** Detect what kind of URL this is. */
export function getMediaType(url: string | null | undefined): MediaType {
  if (!url) return 'none';
  if (/youtube\.com\/watch|youtu\.be\/|youtube\.com\/shorts\//i.test(url)) return 'youtube';
  if (/soundcloud\.com\//i.test(url)) return 'soundcloud';
  if (url.trim()) return 'audio';
  return 'none';
}

/** Extract the YouTube video ID from any YouTube URL format. */
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/shorts\/([^?&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export type EmbedVariant = 'default' | 'background';

/**
 * Convert a raw YouTube or SoundCloud URL to an embed src.
 * `background`: SoundCloud uses visual=false (audio-focused widget); YouTube uses the same embed (parent hides iframe for listen-without-UI).
 * Returns null for plain audio or unrecognised URLs.
 */
export function toEmbedUrl(url: string | null | undefined, variant: EmbedVariant = 'default'): string | null {
  if (!url) return null;
  const type = getMediaType(url);

  if (type === 'youtube') {
    const id = extractYouTubeId(url);
    if (!id) return null;
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? `&origin=${encodeURIComponent(window.location.origin)}`
        : '';
    // enablejsapi + origin allow the IFrame API to report currentTime for the progress bar.
    return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1${origin}`;
  }

  if (type === 'soundcloud') {
    const visual = variant === 'background' ? 'false' : 'true';
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=${visual}`;
  }

  return null;
}
